import type { CrossSectionDataSet, IntakeDataContext } from "./d";
import {
  generateCrossSectionDataSetTitle,
  generateEquipmentDataPoints,
  generateMLEDataPoints
} from "./calculateCrossSectionDataSets";
import { convertSmallLengthUnits } from "@/libs/units/length";
import { getYToolBypassPositioning } from "./getYToolChartDataSet";

/**
 * Generates datasets representing the intake cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the intakes
 */
export const getIntakeChartDataSet = (
  intakeDataContext: IntakeDataContext
): CrossSectionDataSet[][] => {
  const intakeChartSet = [];
  // Radius =$Inputs.F4/2, Center X, Center Y
  const casingInnerRadius = intakeDataContext.casing.innerDiameterIn / 2;
  const casingInnerCenterX = 0;
  const casingInnerCenterY = 0;
  const casingInnerPoints = generateEquipmentDataPoints(
    casingInnerRadius,
    casingInnerCenterX,
    casingInnerCenterY
  );
  const casingDriftRadius = intakeDataContext.casing.driftDiameterIn / 2;
  const casingDriftCenterX = 0;
  const casingDriftCenterY = 0;
  const casingDriftPoints = generateEquipmentDataPoints(
    casingDriftRadius,
    casingDriftCenterX,
    casingDriftCenterY
  );

  for (const intake of intakeDataContext.intakes) {
    const intakeChartDataSet: CrossSectionDataSet[] = [];
    intakeChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing ID",
        intakeDataContext.casing.innerDiameterIn,
        intakeDataContext.lengthUnit
      ),
      xAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      yAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      points: casingInnerPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, intakeDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, intakeDataContext.lengthUnit)
        };
      })
    });
    intakeChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing Drift ID",
        intakeDataContext.casing.driftDiameterIn,
        intakeDataContext.lengthUnit
      ),
      xAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      yAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      points: casingDriftPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, intakeDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, intakeDataContext.lengthUnit)
        };
      })
    });
    // --- Intake Dim ---
    // Radius=$Inputs.D15/2, Center X, Center Y
    const intakeDimRadius = intake.outerDiameterIn / 2;
    const maxEquipmentOuterDiameterIn =
      intakeDataContext.maxEquipmentOuterDiameterIn || 0;
    let intakeDimCenterX: number;
    const intakeDimCenterY = 0;

    if (
      intakeDataContext.hasYTool &&
      intakeDataContext.bypassTubingOuterDiameterIn &&
      intakeDataContext.yToolWidthIn
    ) {
      // Y-Tool Bypass Tubing Dim circle - use shared positioning logic
      const bypassPositioning = getYToolBypassPositioning({
        casingDriftRadius,
        yToolWidthIn: intakeDataContext.yToolWidthIn,
        bypassTubingOuterDiameterIn:
          intakeDataContext.bypassTubingOuterDiameterIn,
        lengthUnit: intakeDataContext.lengthUnit
      });
      intakeChartDataSet.push(bypassPositioning.bypassTubingDataSet);

      // Position intake using Excel formula: ='Y-Tool Cross'!K4
      // Max Equipment Center X = bypassCenterX + bypassRadius + maxEquipmentRadius
      const maxEquipmentDimRadius = maxEquipmentOuterDiameterIn / 2;
      intakeDimCenterX =
        bypassPositioning.bypassTubingDimCenterX +
        bypassPositioning.bypassTubingDimRadius +
        maxEquipmentDimRadius;
    } else {
      // Standard positioning when no y-tool
      intakeDimCenterX = -casingDriftRadius + maxEquipmentOuterDiameterIn / 2;
    }
    const intakeDimPoints = generateEquipmentDataPoints(
      intakeDimRadius,
      intakeDimCenterX,
      intakeDimCenterY
    );
    intakeChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Intake OD",
        intake.outerDiameterIn,
        intakeDataContext.lengthUnit
      ),
      xAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      yAxisLabel: `${intakeDataContext.lengthUnit.label}`,
      points: intakeDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, intakeDataContext.lengthUnit),
          y: convertSmallLengthUnits(pt.y, intakeDataContext.lengthUnit)
        };
      })
    });

    // Only chart MLE and Intake + MLE OD when there's no y-tool
    if (!intakeDataContext.hasYTool) {
      // --- MLE Upper Dim ---
      const mleUpperDimRadius = intakeDataContext.mle.depthIn / 2;
      const mleUpperDimCenterX =
        intakeDimRadius + mleUpperDimRadius + intakeDimCenterX;
      const mleUpperDimCenterY =
        intakeDataContext.mle.widthIn / 2 - intakeDataContext.mle.depthIn / 2;
      // --- MLE Lower Dim ---
      const mleLowerDimRadius = mleUpperDimRadius;
      // Center X same as upper
      const mleLowerDimCenterX = mleUpperDimCenterX;
      const mleLowerDimCenterY = -(
        intakeDataContext.mle.widthIn / 2 -
        intakeDataContext.mle.depthIn / 2
      );
      const mleDimPoints = generateMLEDataPoints(
        mleUpperDimRadius,
        mleUpperDimCenterX,
        mleUpperDimCenterY,
        mleLowerDimRadius,
        mleLowerDimCenterX,
        mleLowerDimCenterY
      );
      intakeChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "MLE Depth",
          intakeDataContext.mle.depthIn,
          intakeDataContext.lengthUnit
        ),
        xAxisLabel: `${intakeDataContext.lengthUnit.label}`,
        yAxisLabel: `${intakeDataContext.lengthUnit.label}`,
        points: mleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, intakeDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, intakeDataContext.lengthUnit)
          };
        })
      });

      // --- Intake + MLE Dim ---
      // Radius =K19+D19
      const maxCalculatedOuterDiameterIn =
        intake.maxCalculatedOuterDiameterIn || 0;
      const intakeAndMleDimRadius = maxCalculatedOuterDiameterIn / 2;
      const intakeAndMleDimCenterX =
        intakeAndMleDimRadius - intakeDimRadius + intakeDimCenterX;
      const intakeAndMleDimCenterY = 0;
      const intakeAndMleDimPoints = generateEquipmentDataPoints(
        intakeAndMleDimRadius,
        intakeAndMleDimCenterX,
        intakeAndMleDimCenterY
      );
      intakeChartDataSet.push({
        title: generateCrossSectionDataSetTitle(
          "Intake + MLE OD",
          maxCalculatedOuterDiameterIn,
          intakeDataContext.lengthUnit
        ),
        xAxisLabel: `${intakeDataContext.lengthUnit.label}`,
        yAxisLabel: `${intakeDataContext.lengthUnit.label}`,
        points: intakeAndMleDimPoints.map(pt => {
          return {
            x: convertSmallLengthUnits(pt.x, intakeDataContext.lengthUnit),
            y: convertSmallLengthUnits(pt.y, intakeDataContext.lengthUnit)
          };
        })
      });
    }
    intakeChartSet.push(intakeChartDataSet);
  }
  return intakeChartSet;
};
